import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import SalarySubNav from './SalarySubNav';
import { apiGet, apiSend } from './api';
import { fmtCurrency, fmtDate } from './formatters';
import type { RemittanceRow } from './types';
import DataTable from '../../shared/components/DataTable';

const RemittanceList: React.FC = () => {
  const { sessionToken } = useAuth();
  const [rows, setRows] = useState<RemittanceRow[]>([]);
  const [err, setErr] = useState('');
  const [markingMonth, setMarkingMonth] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));

  const reload = useCallback(async () => {
    try {
      const res = await apiGet<{ remittances: RemittanceRow[] }>('/remittances', sessionToken);
      setRows(res.remittances);
    } catch (e: any) { setErr(e.message); }
  }, [sessionToken]);

  useEffect(() => { reload(); }, [reload]);

  const markPaid = async (month: string) => {
    try {
      await apiSend(`/remittances/${month}`, 'POST', {
        paid_at: paidAt,
        confirmation_number: confirmation || null,
      }, sessionToken);
      setMarkingMonth(null);
      setConfirmation('');
      reload();
    } catch (e: any) { setErr(e.message); }
  };

  return (
    <div className="salary-module">
      <h2>CRA Remittances (Monthly)</h2>
      <SalarySubNav />
      {err && <div className="error-banner">{err}</div>}

      <div className="salary-card">
        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
          Totals are rolled up from pay periods whose end-date falls in that month.
          Remit to CRA by the 15th of the following month.
        </p>
      </div>

      <div className="salary-card">
        <DataTable
          rows={rows}
          rowKey={(r) => r.period_month}
          caption="CRA remittances"
          moreLabel="Breakdown"
          empty="No pay periods yet."
          columns={[
            { key: 'month', header: 'Month', priority: 'primary', render: (r) => r.period_month },
            { key: 'total', header: 'Total', priority: 'summary', numeric: true, render: (r) => <strong>{fmtCurrency(r.total)}</strong> },
            { key: 'status', header: 'Status', priority: 'summary', render: (r) => <span className={`status-badge status-${r.status}`}>{r.status}</span> },
            { key: 'paid', header: 'Paid', priority: 'summary', render: (r) => (r.paid_at ? fmtDate(r.paid_at) : '—') },
            { key: 'conf', header: 'Confirmation', priority: 'summary', render: (r) => r.confirmation_number || '—' },
            { key: 'tax', header: 'Income Tax', priority: 'detail', numeric: true, render: (r) => fmtCurrency(r.income_tax) },
            { key: 'cppe', header: 'CPP (emp)', priority: 'detail', numeric: true, render: (r) => fmtCurrency(r.cpp_employee) },
            { key: 'cppr', header: 'CPP (er)', priority: 'detail', numeric: true, render: (r) => fmtCurrency(r.cpp_employer) },
            { key: 'eie', header: 'EI (emp)', priority: 'detail', numeric: true, render: (r) => fmtCurrency(r.ei_employee) },
            { key: 'eir', header: 'EI (er)', priority: 'detail', numeric: true, render: (r) => fmtCurrency(r.ei_employer) },
          ]}
          actions={(r) => (
            r.status === 'pending'
              ? <button className="salary-btn" onClick={() => setMarkingMonth(r.period_month)}>Mark Paid</button>
              : null
          )}
        />
      </div>

      {markingMonth && (
        <div className="modal-backdrop" onClick={() => setMarkingMonth(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Mark {markingMonth} as Paid to CRA</h3>
            <div className="form-row">
              <label>Paid at</label>
              <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
            </div>
            <div className="form-row">
              <label>CRA confirmation number</label>
              <input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="salary-btn secondary" onClick={() => setMarkingMonth(null)}>Cancel</button>
              <button className="salary-btn" onClick={() => markPaid(markingMonth)}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemittanceList;
